import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Save, Loader, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} from '../../store/notificationApi';
import { toast } from '@clareefai/ui';

const VENDOR_NOTIFICATION_KEYS = [
  'NEW_PROMOTER_VERIFIED',
  'NEW_PROSPECT_VERIFIED',
  'REVIEW_PUBLISHED',
  'TESTIMONIAL_SUBMITTED',
  'MEETING_SCHEDULED',
  'MEETING_ENDED',
  'MEETING_COMING_SOON',
  'PROSPECT_FINALIZED',
];

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: settings, isLoading } = useGetNotificationSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateNotificationSettingsMutation();

  const handleToggle = async (key: string, value: boolean) => {
    const newSettings = { ...(settings?.settings ?? {}), [key]: value };
    try {
      await updateSettings({ settings: newSettings }).unwrap();
      toast.success(t('settings.notifications.saved'));
    } catch {
      toast.error(t('settings.notifications.saveFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const vendorTypes = VENDOR_NOTIFICATION_KEYS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('settings.notifications.backToSettings')}
        </button>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Bell className="w-6 h-6" />
            {t('settings.notifications.centerTitle')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('settings.notifications.centerDescription')}
          </p>

          <div className="space-y-4">
            {vendorTypes.map((key) => {
              const enabled = settings?.settings[key] ?? true;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-semibold text-gray-900">
                      {t(`settings.notifications.labels.${key}`, { defaultValue: key })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle(key, !enabled)}
                    disabled={isUpdating}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        enabled ? 'transform translate-x-6' : ''
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
